/**
 * Origen de la API, sesión y credencial heredada.
 *
 * EL ORIGEN
 *
 * En desarrollo el cliente y la API comparten origen: Vite hace de proxy de
 * /api al puerto 3003, así que basta con rutas relativas. En producción también
 * comparten origen (un solo proceso Node sirve las dos cosas), pero se deja
 * `VITE_API_URL` por si algún día se separan.
 *
 * EL TOKEN DE ACCESO VIVE EN MEMORIA, NO EN localStorage
 *
 * Es la decisión importante de este fichero. Guardarlo en `localStorage` lo deja
 * al alcance de cualquier script que se cuele en la página; en memoria muere con
 * la pestaña. Lo que sí persiste es el REFRESCO, y en web va en una cookie
 * httpOnly que el JavaScript no puede leer.
 *
 * El precio es que al recargar no hay token: se pide uno nuevo con el refresco
 * al arrancar. Eso es un viaje de ida y vuelta, no un problema.
 *
 * EN LA APP ENVUELTA NO HAY COOKIE
 *
 * El WebView rechaza la cookie httpOnly de origen cruzado, así que ahí el
 * refresco viaja en el cuerpo y se guarda en almacenamiento. No es tan seguro
 * como la cookie, pero es lo único que funciona -- y en una app el almacén no
 * está expuesto a scripts de terceros como lo está una página web.
 */
import { invalidarPorMutacion } from './cache.js';

const BASE = (import.meta.env?.VITE_API_URL ?? '').replace(/\/+$/, '');

const REFRESH_KEY = 'ptcg-tracker:refresh';    // sólo se usa en la app envuelta

/** Construye la URL de un endpoint. `path` siempre empieza por /api. */
export const apiUrl = (path) => `${BASE}${path}`;

const store = {
  get(k) {
    try {
      return window.localStorage.getItem(k) ?? '';
    } catch {
      return '';
    }
  },
  set(k, v) {
    try {
      if (v) window.localStorage.setItem(k, v);
      else window.localStorage.removeItem(k);
    } catch {
      /* modo privado o almacenamiento bloqueado */
    }
  },
};

// --- Clave compartida heredada ----------------------------------------------


// --- Sesión ------------------------------------------------------------------

/**
 * ¿Estamos dentro de la app envuelta?
 *
 * Capacitor sirve la página desde `capacitor://` o `https://localhost`. Se
 * detecta por el origen para no depender de que la librería esté cargada.
 */
export const isNativeApp = () =>
  typeof window !== 'undefined' &&
  (window.Capacitor?.isNativePlatform?.() === true ||
    ['capacitor:', 'ionic:'].includes(window.location.protocol) ||
    window.location.origin === 'https://localhost');

let accessToken = null;
let onSessionChange = () => {};

export const setAccessToken = (t) => {
  accessToken = t ?? null;
};
export const getAccessToken = () => accessToken;
export const onSession = (fn) => {
  onSessionChange = fn ?? (() => {});
};

const nativeRefresh = () => (isNativeApp() ? store.get(REFRESH_KEY) : '');
const saveNativeRefresh = (t) => {
  if (isNativeApp()) store.set(REFRESH_KEY, t ?? '');
};

function headersFor(options) {
  const h = { ...(options.headers ?? {}) };
  if (accessToken) h.authorization = `Bearer ${accessToken}`;
  if (isNativeApp()) {
    // Le dice al servidor que devuelva el refresco en el cuerpo, porque aquí la
    // cookie no va a llegar.
    h['x-client'] = 'app';
    const rt = nativeRefresh();
    if (rt) h['x-refresh-token'] = rt;
  }
  return h;
}

/**
 * Canjea el refresco por un token nuevo.
 *
 * `pending` evita la estampida: si cinco peticiones reciben 401 a la vez, se
 * refresca UNA vez y las cinco esperan a esa. Sin ello, cada una rotaría el
 * refresco y las demás se quedarían con uno ya invalidado -- cerrando la sesión
 * justo cuando se intentaba salvarla.
 */
let pending = null;
export function refreshSession() {
  pending ??= fetch(apiUrl('/api/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
    headers: headersFor({}),
  })
    /*
     * SOLO UN 401 CIERRA LA SESIÓN. Un 5xx (Hostinger redespliega con cada
     * push), un 429 o la red caída no dicen nada del refresco, que sigue
     * valiendo: tratarlos como «sin sesión» enseñaba la app deslogeada con la
     * cookie perfecta (pasó en la de escritorio, sep-2026). Esos fallos
     * devuelven `undefined` sin tocar el estado — distinto del `null` del 401,
     * para que el arranque sepa que merece reintentar.
     */
    .then((r) => {
      if (r.ok) return r.json();
      if (r.status === 401) return null;
      throw new Error(`refresh ${r.status}`);
    })
    .then((data) => {
      accessToken = data?.accessToken ?? null;
      if (data?.refreshToken) saveNativeRefresh(data.refreshToken);
      if (!data) saveNativeRefresh('');
      onSessionChange(data?.user ?? null);
      return data;
    })
    .catch(() => undefined)
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function clearSession() {
  accessToken = null;
  saveNativeRefresh('');
}

/**
 * fetch con el origen resuelto, la sesión adjunta y un reintento si caducó.
 *
 * El reintento se salta las propias rutas de sesión: si `/login` responde 401 es
 * que la contraseña está mal, y refrescar para reintentar sería un bucle.
 */
export async function api(path, options = {}) {
  const send = () =>
    fetch(apiUrl(path), { ...options, credentials: 'include', headers: headersFor(options) });

  let res = await send();
  if (res.status === 401 && !path.startsWith('/api/auth/') && (accessToken || nativeRefresh())) {
    const data = await refreshSession();
    if (data) res = await send();
  } else if (res.status === 403 && !path.startsWith('/api/auth/') && (accessToken || nativeRefresh())) {
    /*
     * Un 403 de plan tambien puede ser un token muerto, no un plan que falta.
     *
     * Los endpoints Pro comprueban el plan ANTES de nada: con un token caducado
     * o invalidado (el servidor se reinicio y roto el secreto) el usuario pasa
     * por anonimo, y anonimo es plan gratis -- asi que responden 403
     * "pro_required" en vez de 401. Sin este reintento, una cuenta Pro con la
     * pestaña abierta un rato veia "necesitas Pro" en todo hasta recargar: es
     * exactamente el sintoma reportado en produccion. Se refresca UNA vez y se
     * repite; si de verdad no hay Pro, el segundo 403 es el definitivo.
     */
    const cuerpo = await res.clone().json().catch(() => null);
    if (cuerpo?.error === 'pro_required') {
      const data = await refreshSession();
      if (data) res = await send();
    }
  }
  /*
   * Toda mutación que salió bien invalida lo cacheado de ese recurso, AQUÍ y
   * no en cada componente: quien guarda, copia o borra no tiene que saber que
   * existe una caché, y ningún sitio nuevo puede olvidarse de avisar.
   */
  if (res.ok && (options.method ?? 'GET').toUpperCase() !== 'GET') invalidarPorMutacion(path);
  return res;
}
